import {
  Box,
  Button,
  Switch,
  Text,
  Flex,
  Image,
  VStack,
  HStack,
  useToast,
  Divider
} from '@chakra-ui/react';
import { FC, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import OlaGGFormInput from '../../components/Forms/OlaggFormInput';
import OlaggForm from '../../components/Forms/OlaggForm';
import OlaggFormSelect from '../../components/Forms/OlaggFormSelect';
import { AdminLevelReward } from '@olagg/db-types';
import useLevelRewards from './hooks/useLevelRewards';
import { levelRewardSchema, LevelRewardFormData } from '@olagg/validation-schemas';
import usePrizes from '../ClawMachine/hooks/usePrizes';

type IProps = {
  initialData?: AdminLevelReward | undefined;
  onSubmit: (data: LevelRewardFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

type MultilingualText = {
  es: string;
  en?: string;
  pt?: string;
};

type MultilingualIcon = {
  es?: string;
  en?: string;
  pt?: string;
};

const buildText = (levelReward?: AdminLevelReward): MultilingualText => ({
  es: levelReward?.title || '',
  en: levelReward?.extraData?.en?.title || '',
  pt: levelReward?.extraData?.pt?.title || ''
});

const buildIcon = (levelReward?: AdminLevelReward): MultilingualIcon => ({
  es: levelReward?.rewardConfig?.icon || '',
  en: levelReward?.extraData?.en?.icon || '',
  pt: levelReward?.extraData?.pt?.icon || ''
});

const LevelRewardsForm: FC<IProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  isEdit = false
}) => {
  const toast = useToast();
  const rewardTypesOptions = [
    { label: 'XP Bonus', value: 'xp_bonus' },
    { label: 'Blockchain Token', value: 'blockchain_token' },
    { label: 'Treasure Opportunity', value: 'treasure_opportunity' },
    { label: 'Claw Coin', value: 'claw_coin' },
    { label: 'Store Discount', value: 'store_discount' },
    { label: 'Custom Reward', value: 'custom_reward' }
  ];
  
  const [otherLangs, setOtherLangs] = useState<boolean>(
    !!(initialData?.extraData?.en || initialData?.extraData?.pt)
  );
  const [text, setText] = useState<MultilingualText>(buildText(initialData));
  const [icon, setIcon] = useState<MultilingualIcon>(buildIcon(initialData));
  const { getBlockchainNetworks, getBlockchainTokens } = usePrizes();
  
  // Estado para las opciones de los selectores
  const [networkOptions, setNetworkOptions] = useState<{value: number, label: string}[]>([]);
  const [tokenOptions, setTokenOptions] = useState<{value: number, label: string}[]>([]);
  const [blockchainNetworkFilter, setBlockchainNetworkFilter] = useState<string | undefined>(
    initialData?.rewardConfig?.networkId ? String(initialData.rewardConfig.networkId) : undefined
  );
  const [loadingTokens, setLoadingTokens] = useState(false);

  const [hasTranslations, setHasTranslations] = useState<boolean>(
    !!(initialData?.extraData?.en || initialData?.extraData?.pt)
  );

  const checkForTranslations = () => {
    const hasEnglish = !!(text.en || icon.en);
    const hasPortuguese = !!(text.pt || icon.pt);
    const hasExistingTranslations = !!(initialData?.extraData?.en || initialData?.extraData?.pt);
    
    return hasEnglish || hasPortuguese || hasExistingTranslations;
  };

  useEffect(() => {
    setHasTranslations(checkForTranslations());
  }, [text, icon]);

  const formatOptionsResponse = useCallback((response: any[]): {value: number, label: string}[] => {
    if (!Array.isArray(response)) return [];
    return response.map((item: any) => ({
      value: item.id,
      label: item.name || item.title || 'Sin nombre'
    }));
  }, []);

  const loadBlockchainNetworkOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined
      };
      const response = await getBlockchainNetworks(params);
      const formatted = formatOptionsResponse(response);
      return formatted;
    } catch (error) {
      console.error('Error loading blockchain network options:', error);
      return [];
    }
  }, [getBlockchainNetworks, formatOptionsResponse]);

  const loadBlockchainTokenOptions = useCallback(async (inputValue: string, networkId?: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined,
        network: networkId || undefined
      };
      const response = await getBlockchainTokens(params);
      console.log('Tokens response:', response);
      console.log('Filtering tokens for networkId:', networkId);
      
      // Filtrar por networkId si se proporciona
      const filteredResponse = networkId 
        ? response.filter((token: any) => token.network?.id === networkId)
        : response;
      
      const formatted = formatOptionsResponse(filteredResponse);
      return formatted;
    } catch (error) {
      console.error('Error loading blockchain token options:', error);
      return [];
    }
  }, [getBlockchainTokens, formatOptionsResponse]);

  // Cargar opciones iniciales
  useEffect(() => {
    const loadInitialOptions = async () => {
      try {
        // Cargar redes
        const networks = await loadBlockchainNetworkOptions('');
        setNetworkOptions(networks);

        // Si estamos editando y es blockchain_token, cargar los tokens filtrados
        if (isEdit && initialData?.rewardType === 'blockchain_token' && initialData?.rewardConfig?.networkId) {
          const tokens = await loadBlockchainTokenOptions('', initialData.rewardConfig.networkId);
          setTokenOptions(tokens);
        }
      } catch (error) {
        console.error('Error loading initial options:', error);
      }
    };

    loadInitialOptions();
  }, []);

  const useFormMethods = useForm<LevelRewardFormData>({
    resolver: yupResolver(levelRewardSchema),
    mode: 'onChange',
    defaultValues: {
      title: initialData?.title || '',
      level: initialData?.level || 1,
      rewardType: initialData?.rewardType || undefined,
      rewardConfig: initialData?.rewardConfig || {},
      active: initialData?.active !== undefined ? initialData.active : true,
      priority: initialData?.priority || 0,
      extraData: initialData?.extraData || {}
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset
  } = useFormMethods;

  const watchedRewardType = watch('rewardType');
  const watchedNetworkId = watch('rewardConfig.networkId');
  const watchedIcon = watch('rewardConfig.icon');

  // Cargar tokens cuando cambia el network
  useEffect(() => {
    if (watchedRewardType === 'blockchain_token' && watchedNetworkId && !loadingTokens) {
      setLoadingTokens(true);
      setBlockchainNetworkFilter(String(watchedNetworkId));
      
      const loadTokensByNetwork = async () => {
        try {
          const tokens = await loadBlockchainTokenOptions('', watchedNetworkId);
          setTokenOptions(tokens);
          
          // Si no estamos editando o si cambiamos de red, resetear el token seleccionado
          if (!isEdit || watchedNetworkId !== initialData?.rewardConfig?.networkId) {
            setValue('rewardConfig.tokenId', undefined);
          }
        } catch (error) {
          console.error('Error loading tokens by network:', error);
          toast({
            title: 'Error',
            description: 'No se pudieron cargar los tokens de la red seleccionada',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        } finally {
          setLoadingTokens(false);
        }
      };

      loadTokensByNetwork();
    }
  }, [watchedNetworkId, watchedRewardType]);

  // Resetear campos cuando cambia el rewardType
  useEffect(() => {
    if (!isEdit || (isEdit && watchedRewardType !== initialData?.rewardType)) {
      setValue('rewardConfig', {});
      setValue('extraData', {});
      setBlockchainNetworkFilter(undefined);
      setTokenOptions([]);
      // Resetear estados locales de traducciones
      setText({ es: '', en: '', pt: '' });
      setIcon({ es: '', en: '', pt: '' });
      setOtherLangs(false);
    }
  }, [watchedRewardType, setValue, isEdit, initialData?.rewardType]);

  const beforeSubmit = async (data: LevelRewardFormData) => {
    const baseData: LevelRewardFormData = {
      ...data,
      extraData: {
        en: {
          title: text.en || '',
          icon: watchedRewardType === 'custom_reward' ? (icon.en || '') : undefined,
        },
        pt: {
          title: text.pt || '',
          icon: watchedRewardType === 'custom_reward' ? (icon.pt || '') : undefined,
        }
      }
    };

    await onSubmit(baseData);
  };

  const renderRewardConfigFields = () => {
    switch (watchedRewardType) {
      case 'xp_bonus':
        return (
          <OlaGGFormInput
            controlName="rewardConfig.amount"
            label="Cantidad de XP"
            placeholder="Ingrese la cantidad de XP"
            inputType="number"
            required
          />
        );

      case 'blockchain_token':
        return (
          <VStack spacing={4} align="stretch">
            <OlaggFormSelect
              controlName="rewardConfig.networkId"
              label="Red Blockchain"
              placeholder="Seleccione una red"
              staticOptions={networkOptions}
              required
              newVersion
            />
            
            {watchedNetworkId && (
              <>
                <OlaggFormSelect
                  controlName="rewardConfig.tokenId"
                  label="Token"
                  placeholder={loadingTokens ? "Cargando tokens..." : "Seleccione un token"}
                  staticOptions={tokenOptions}
                  required
                  newVersion
                />
                
                <OlaGGFormInput
                  controlName="rewardConfig.quantity"
                  label="Cantidad"
                  placeholder="Ingrese la cantidad de tokens (ej: 1,5 o 1.5)"
                  inputType="text"
                  required
                />
              </>
            )}
          </VStack>
        );

      case 'treasure_opportunity':
        return (
          <OlaGGFormInput
            controlName="rewardConfig.amount"
            label="Cantidad de Oportunidades"
            placeholder="Ingrese la cantidad de oportunidades"
            inputType="number"
            required
          />
        );

      case 'claw_coin':
        return (
          <VStack spacing={4} align="stretch">
            <OlaGGFormInput
              controlName="rewardConfig.amount"
              label="Cantidad de Monedas"
              placeholder="Ingrese la cantidad de monedas"
              inputType="number"
              required
            />
            
            <OlaGGFormInput
              controlName="rewardConfig.expirationDays"
              label="Días hasta expiración (Opcional)"
              placeholder="Ingrese los días de validez de las monedas"
              inputType="number"
            />
          </VStack>
        );

      case 'store_discount':
        return (
          <VStack spacing={4} align="stretch">
            <OlaGGFormInput
              controlName="rewardConfig.discountPercentage"
              label="Porcentaje de Descuento (%)"
              placeholder="Ingrese el porcentaje (1-100)"
              inputType="number"
              required
            />
            
            <OlaGGFormInput
              controlName="rewardConfig.discountLimit"
              label="Límite de descuento"
              placeholder="Ingrese el límite de descuento"
              inputType="number"
              required
            />
            
            <OlaGGFormInput
              controlName="rewardConfig.discountExpirationDays"
              label="Días hasta expiración"
              placeholder="Ingrese los días de validez del descuento"
              inputType="number"
              required
            />
          </VStack>
        );

      case 'custom_reward':
        return (
          <VStack spacing={4} align="stretch">
            <OlaGGFormInput
              controlName="rewardConfig.icon"
              label="Ícono (Español)"
              placeholder="URL del ícono"
              inputType="text"
              required
            />

            {watchedIcon && (
              <Image
                src={watchedIcon}
                alt="Preview"
                maxW="200px"
                maxH="200px"
                objectFit="contain"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.600"
              />
            )}

            <OlaGGFormInput
              controlName="rewardConfig.link"
              label="Enlace (Opcional)"
              placeholder="https://ejemplo.com"
              inputType="text"
            />
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <OlaggForm
      onSubmit={beforeSubmit}
      useFormMethods={useFormMethods}
      style={{
        width: '100%'
      }}
    >
      <VStack spacing={6} align="stretch" maxW='600px' margin='0 auto'>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color="white" mb={4}>
            {isEdit ? 'Editar Premio por Nivel' : 'Crear Premio por Nivel'}
          </Text>
        </Box>

        <OlaGGFormInput
          controlName="level"
          label="Nivel"
          placeholder="Ingrese el nivel"
          inputType="number"
          required
        />

        <OlaGGFormInput
          controlName="title"
          label="Título"
          placeholder="Ingrese el título del premio"
          inputType="text"
          required
        />

        <OlaggFormSelect
          controlName="rewardType"
          label="Tipo de Recompensa"
          placeholder="Seleccione el tipo de recompensa"
          staticOptions={rewardTypesOptions}
          required
          newVersion
        />

        {watchedRewardType && (
          <Box
            p={4}
            bg="gray.800"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.600"
          >
            <Text fontSize="md" fontWeight="bold" color="white" mb={4}>
              Configuración de la Recompensa
            </Text>
            {renderRewardConfigFields()}
          </Box>
        )}

        {watchedRewardType && (
          <Box>
            <Divider mb={4} />
            
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="md" fontWeight="bold" color="white">
                Traducciones del Título
              </Text>
              <Switch
                isChecked={otherLangs}
                onChange={(e) => setOtherLangs(e.target.checked)}
                colorScheme="purple"
              />
            </Flex>

            {otherLangs && (
              <VStack spacing={4} align="stretch" p={4} bg="gray.800" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold" color="gray.300">
                  Inglés
                </Text>
                
                <Box>
                  <Text fontSize="sm" color="gray.400" mb={1}>Título</Text>
                  <input
                    type="text"
                    value={text.en || ''}
                    onChange={(e) => setText({ ...text, en: e.target.value })}
                    placeholder="Título en inglés"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #4A5568',
                      backgroundColor: '#2D3748',
                      color: 'white'
                    }}
                  />
                </Box>

                {watchedRewardType === 'custom_reward' && (
                  <>
                    <Box>
                      <Text fontSize="sm" color="gray.400" mb={1}>Ícono (URL)</Text>
                      <input
                        type="text"
                        value={icon.en || ''}
                        onChange={(e) => setIcon({ ...icon, en: e.target.value })}
                        placeholder="URL del ícono en inglés"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #4A5568',
                          backgroundColor: '#2D3748',
                          color: 'white'
                        }}
                      />
                    </Box>

                    {icon.en && (
                      <Image
                        src={icon.en}
                        alt="Preview EN"
                        maxW="200px"
                        maxH="200px"
                        objectFit="contain"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.600"
                      />
                    )}
                  </>
                )}

                <Divider />

                <Text fontSize="sm" fontWeight="bold" color="gray.300">
                  Portugués
                </Text>
                
                <Box>
                  <Text fontSize="sm" color="gray.400" mb={1}>Título</Text>
                  <input
                    type="text"
                    value={text.pt || ''}
                    onChange={(e) => setText({ ...text, pt: e.target.value })}
                    placeholder="Título en portugués"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #4A5568',
                      backgroundColor: '#2D3748',
                      color: 'white'
                    }}
                  />
                </Box>

                {watchedRewardType === 'custom_reward' && (
                  <>
                    <Box>
                      <Text fontSize="sm" color="gray.400" mb={1}>Ícono (URL)</Text>
                      <input
                        type="text"
                        value={icon.pt || ''}
                        onChange={(e) => setIcon({ ...icon, pt: e.target.value })}
                        placeholder="URL del ícono en portugués"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #4A5568',
                          backgroundColor: '#2D3748',
                          color: 'white'
                        }}
                      />
                    </Box>

                    {icon.pt && (
                      <Image
                        src={icon.pt}
                        alt="Preview PT"
                        maxW="200px"
                        maxH="200px"
                        objectFit="contain"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.600"
                      />
                    )}
                  </>
                )}
              </VStack>
            )}
          </Box>
        )}

        <OlaGGFormInput
          controlName="priority"
          label="Prioridad"
          placeholder="Ingrese la prioridad (mayor número = mayor prioridad)"
          inputType="number"
          required
        />

        <Flex align="center" justify="space-between">
          <Text fontSize="md" fontWeight="bold" color="white">
            Activo
          </Text>
          <Switch
            {...register('active')}
            defaultChecked={initialData?.active !== undefined ? initialData.active : true}
            colorScheme="purple"
          />
        </Flex>

        <HStack spacing={4} justify="flex-end" mt={6}>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={onCancel}
            isDisabled={isSubmitting || loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            colorScheme="purple"
            isLoading={isSubmitting || loading}
            loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
          >
            {isEdit ? 'Actualizar' : 'Crear'}
          </Button>
        </HStack>
      </VStack>
    </OlaggForm>
  );
};

export default LevelRewardsForm;